import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Key, UserPlus, Trash2, User, Users, Shield, RefreshCw } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Settings() {
  const { user, getAuthHeader, isAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  
  // Change password state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Add user state
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [newUserRole, setNewUserRole] = useState('usuario');
  const [addingUser, setAddingUser] = useState(false);

  // Reset password state
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetUsername, setResetUsername] = useState('');
  const [resetNewPassword, setResetNewPassword] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);

  useEffect(() => {
    if (isAdmin()) {
      loadUsers();
    }
  }, []);

  const loadUsers = async () => {
    if (!isAdmin()) return;
    try {
      const response = await axios.get(`${API}/users`, {
        headers: getAuthHeader()
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Error al cargar usuarios');
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('Completa todos los campos');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    
    if (newPassword.length < 4) {
      toast.error('La contraseña debe tener al menos 4 caracteres');
      return;
    }

    setChangingPassword(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      }, {
        headers: getAuthHeader()
      });
      toast.success('Contraseña actualizada correctamente');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar contraseña');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    
    if (!newUsername || !newUserPassword) {
      toast.error('Completa todos los campos');
      return;
    }
    
    if (newUserPassword.length < 4) {
      toast.error('La contraseña debe tener al menos 4 caracteres');
      return;
    }

    setAddingUser(true);
    try {
      await axios.post(`${API}/users`, {
        username: newUsername,
        password: newUserPassword,
        role: newUserRole
      }, {
        headers: getAuthHeader()
      });
      toast.success('Usuario creado correctamente');
      setShowAddUser(false);
      setNewUsername('');
      setNewUserPassword('');
      setNewUserRole('usuario');
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear usuario');
    } finally {
      setAddingUser(false);
    }
  };

  const handleDeleteUser = async (username) => {
    if (!window.confirm(`¿Eliminar el usuario "${username}"?`)) return;
    
    try {
      await axios.delete(`${API}/users/${username}`, {
        headers: getAuthHeader()
      });
      toast.success('Usuario eliminado');
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar usuario');
    }
  };

  const handleChangeRole = async (username, newRole) => {
    try {
      await axios.put(`${API}/users/${username}/role?role=${newRole}`, {}, {
        headers: getAuthHeader()
      });
      toast.success(`Rol actualizado a "${newRole}"`);
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar rol');
    }
  };

  const openResetPassword = (username) => {
    setResetUsername(username);
    setResetNewPassword('');
    setShowResetPassword(true);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (!resetNewPassword) {
      toast.error('Introduce la nueva contraseña');
      return;
    }
    
    if (resetNewPassword.length < 4) {
      toast.error('La contraseña debe tener al menos 4 caracteres');
      return;
    }

    setResettingPassword(true);
    try {
      await axios.post(`${API}/users/${resetUsername}/reset-password`, {
        new_password: resetNewPassword
      }, {
        headers: getAuthHeader()
      });
      toast.success(`Contraseña de "${resetUsername}" reseteada`);
      setShowResetPassword(false);
      setResetUsername('');
      setResetNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al resetear contraseña');
    } finally {
      setResettingPassword(false);
    }
  };

  const getRoleBadge = (role) => {
    if (role === 'admin') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 text-amber-800">
          <Shield size={10} />
          Admin
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-600">
        <User size={10} />
        Usuario
      </span>
    );
  };

  return (
    <div className="p-4 space-y-4 max-w-4xl">
      <div>
        <h1 className="text-xl md:text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Configuración</h1>
        <p className="text-xs text-muted-foreground mt-1">
          {isAdmin() ? 'Gestión de cuenta y usuarios' : 'Gestión de cuenta'}
        </p>
      </div>

      <div className={`grid grid-cols-1 ${isAdmin() ? 'lg:grid-cols-2' : ''} gap-4`}>
        {/* Change Password */}
        <Card className="p-3 md:p-4">
          <div className="flex items-center gap-2 mb-3 md:mb-4">
            <Key size={18} className="text-primary" />
            <h2 className="text-sm font-semibold">Cambiar Contraseña</h2>
          </div>
          <form onSubmit={handleChangePassword} className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="current" className="text-xs">Contraseña actual</Label>
              <Input
                id="current"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Tu contraseña actual"
                className="text-sm h-9"
                data-testid="current-password"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="new" className="text-xs">Nueva contraseña</Label>
              <Input
                id="new"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Nueva contraseña"
                className="text-sm h-9"
                data-testid="new-password"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="confirm" className="text-xs">Confirmar contraseña</Label>
              <Input
                id="confirm"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Repite la nueva contraseña"
                className="text-sm h-9"
                data-testid="confirm-password"
              />
            </div>
            <Button
              type="submit"
              size="sm"
              className="w-full"
              disabled={changingPassword}
              data-testid="change-password-btn"
            >
              {changingPassword ? 'Cambiando...' : 'Cambiar Contraseña'}
            </Button>
          </form>
        </Card>

        {/* User Management - Solo visible para admins */}
        {isAdmin() && (
          <Card className="p-3 md:p-4">
            <div className="flex items-center justify-between mb-3 md:mb-4">
              <div className="flex items-center gap-2">
                <Users size={18} className="text-primary" />
                <h2 className="text-sm font-semibold">Usuarios</h2>
              </div>
              <Button 
                size="sm" 
                variant="outline"
                className="text-xs h-7"
                onClick={() => setShowAddUser(true)}
                data-testid="add-user-btn"
              >
                <UserPlus size={14} className="mr-1" />
                Añadir
              </Button>
            </div>
            
            {loadingUsers ? (
              <p className="text-xs text-muted-foreground text-center py-4">Cargando...</p>
            ) : (
              <div className="space-y-2">
                {users.map((u) => (
                  <div 
                    key={u.username} 
                    className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-2 bg-muted/30 rounded-lg"
                    data-testid={`user-${u.username}`}
                  >
                    <div className="flex items-center gap-2">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-medium">{u.username}</p>
                          {getRoleBadge(u.role)}
                        </div>
                        <p className="text-[10px] text-muted-foreground">
                          {u.created_at ? new Date(u.created_at).toLocaleDateString('es-ES') : '-'}
                        </p>
                      </div>
                    </div>
                    {u.username !== user?.username && (
                    <div className="flex items-center gap-1 self-end sm:self-auto">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-muted-foreground hover:text-primary"
                        onClick={() => openResetPassword(u.username)}
                        title="Resetear contraseña"
                        data-testid={`reset-password-${u.username}`}
                      >
                        <RefreshCw size={14} />
                      </Button>
                      <Select
                        value={u.role}
                        onValueChange={(value) => handleChangeRole(u.username, value)}
                      >
                        <SelectTrigger className="h-7 w-20 md:w-24 text-xs" data-testid={`role-select-${u.username}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="usuario">Usuario</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                        onClick={() => handleDeleteUser(u.username)}
                        data-testid={`delete-user-${u.username}`}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          </Card>
        )}
      </div>

      {/* Add User Dialog */}
      <Dialog open={showAddUser} onOpenChange={setShowAddUser}>
        <DialogContent className="max-w-sm" data-testid="add-user-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">Añadir Usuario</DialogTitle>
            <DialogDescription className="text-xs">
              Crea un nuevo usuario para acceder al sistema
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddUser} className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="username" className="text-xs">Usuario</Label>
              <Input
                id="username"
                type="text"
                value={newUsername}
                onChange={(e) => setNewUsername(e.target.value)}
                placeholder="Nombre de usuario"
                className="text-sm h-8"
                data-testid="new-user-username"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="userpassword" className="text-xs">Contraseña</Label>
              <Input
                id="userpassword"
                type="password"
                value={newUserPassword}
                onChange={(e) => setNewUserPassword(e.target.value)}
                placeholder="Contraseña"
                className="text-sm h-8"
                data-testid="new-user-password"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="userrole" className="text-xs">Rol</Label>
              <Select value={newUserRole} onValueChange={setNewUserRole}>
                <SelectTrigger className="text-sm h-8" data-testid="new-user-role">
                  <SelectValue placeholder="Selecciona rol" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="usuario">Usuario</SelectItem>
                  <SelectItem value="admin">Administrador</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowAddUser(false)}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                size="sm"
                disabled={addingUser}
                data-testid="create-user-btn"
              >
                {addingUser ? 'Creando...' : 'Crear Usuario'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={showResetPassword} onOpenChange={setShowResetPassword}>
        <DialogContent className="max-w-sm" data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">Resetear Contraseña</DialogTitle>
            <DialogDescription className="text-xs">
              Nueva contraseña para <strong>{resetUsername}</strong>
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleResetPassword} className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="resetpassword" className="text-xs">Nueva contraseña</Label>
              <Input
                id="resetpassword"
                type="password"
                value={resetNewPassword}
                onChange={(e) => setResetNewPassword(e.target.value)}
                placeholder="Nueva contraseña"
                className="text-sm h-8"
                data-testid="reset-new-password"
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowResetPassword(false)}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                size="sm"
                disabled={resettingPassword}
                data-testid="confirm-reset-btn"
              >
                {resettingPassword ? 'Reseteando...' : 'Resetear'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
